<template>
  <section class="products-wrapper" aria-label="Product management">
    <!-- HEADER -->
    <header class="header">
      <div class="header-top">
        <h1 class="page-title">All Products</h1>
        <p v-if="!loading" class="results-summary">
          {{ resultsSummary }}
        </p>
      </div>

      <div class="header-actions">
        <div class="search-wrap">
          <i class="fa-solid fa-magnifying-glass search-icon" aria-hidden="true"></i>
          <input
            v-model="search"
            type="search"
            class="search-box"
            placeholder="Search by name or brand..."
            aria-label="Search products"
            autocomplete="off"
          />
          <button
            v-if="search"
            type="button"
            class="clear-search"
            aria-label="Clear search"
            @click="search = ''"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <select
          v-model="filterCategory"
          class="sort-select"
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          <option
            v-for="(label, id) in categoryMap"
            :key="id"
            :value="id"
          >
            {{ label }}
          </option>
        </select>

        <select
          v-model="sortBy"
          class="sort-select"
          aria-label="Sort products"
        >
          <option value="name">Name A–Z</option>
          <option value="price_low">Price: low to high</option>
          <option value="price_high">Price: high to low</option>
          <option value="brand">Brand</option>
          <option value="date_new">Newest first</option>
          <option value="date_old">Oldest first</option>
        </select>

        <div class="per-page-wrap">
          <label for="per-page" class="per-page-label">Per page</label>
          <select
            id="per-page"
            v-model.number="perPage"
            class="sort-select per-page-select"
            aria-label="Products per page"
          >
            <option :value="8">8</option>
            <option :value="16">16</option>
            <option :value="24">24</option>
            <option :value="48">48</option>
          </select>
        </div>
      </div>
    </header>

    <!-- LOADING SKELETON -->
    <div v-if="loading" class="products-grid skeleton-grid" aria-busy="true" aria-live="polite">
      <div
        v-for="n in 8"
        :key="'skeleton-' + n"
        class="product-card skeleton-card"
      >
        <div class="skeleton-image"></div>
        <div class="skeleton-details">
          <div class="skeleton-line skeleton-title"></div>
          <div class="skeleton-line skeleton-price"></div>
        </div>
      </div>
    </div>

    <!-- PRODUCT GRID -->
    <transition-group
      v-else
      name="fade-scale"
      tag="div"
      class="products-grid"
      role="list"
    >
      <article
        v-for="product in paginatedProducts"
        :key="product.item_id"
        class="product-card"
        role="listitem"
      >
        <div class="image-container">
          <img
            :src="product.thumbnail || placeholderImage"
            :alt="product.name"
            class="product-img"
            loading="lazy"
            decoding="async"
            @error="onImageError"
          />
          <div v-if="product.is_active === false" class="badge badge-inactive">Inactive</div>
          <div v-else-if="isLowStock(product)" class="badge badge-low-stock">Low stock</div>
          <div class="overlay">
            <div class="overlay-icons">
              <button
                type="button"
                class="overlay-btn"
                :title="'Edit ' + product.name"
                :aria-label="'Edit ' + product.name"
                @click="openEdit(product)"
              >
                <i class="fa-solid fa-pen" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="overlay-btn overlay-btn-delete"
                :title="'Delete ' + product.name"
                :aria-label="'Delete ' + product.name"
                @click.stop="openDeleteModal(product)"
              >
                <i class="fa-solid fa-trash" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="product-details">
          <p v-if="product.brand" class="product-brand">{{ product.brand }}</p>
          <h3 class="product-name">{{ product.name }}</h3>
          <p v-if="categoryLabel(product)" class="product-category">{{ categoryLabel(product) }}</p>
          <p class="product-price">
            {{ formatPrice(product) }}
            <span v-if="priceWas(product)" class="price-was">{{ priceWas(product) }}</span>
          </p>
        </div>
      </article>
    </transition-group>

    <!-- PAGINATION -->
    <nav
      v-if="totalPages > 1 && !loading"
      class="pagination"
      aria-label="Products pagination"
    >
      <button
        type="button"
        class="pagination-btn"
        :disabled="page === 1"
        aria-label="Previous page"
        @click="page = Math.max(1, page - 1)"
      >
        <i class="fa-solid fa-chevron-left"></i> Previous
      </button>
      <span class="pagination-info">
        Page <strong>{{ page }}</strong> of <strong>{{ totalPages }}</strong>
      </span>
      <button
        type="button"
        class="pagination-btn"
        :disabled="page >= totalPages"
        aria-label="Next page"
        @click="page = Math.min(totalPages, page + 1)"
      >
        Next <i class="fa-solid fa-chevron-right"></i>
      </button>
    </nav>

    <!-- EMPTY STATE -->
    <div v-if="!loading && filteredProducts.length === 0" class="empty-state">
      <div class="empty-icon" aria-hidden="true">
        <i class="fa-solid fa-box-open"></i>
      </div>
      <h2 class="empty-title">No products found</h2>
      <p class="empty-text">
        {{ search || filterCategory ? 'Try adjusting your search or filters.' : 'Products you add will appear here.' }}
      </p>
      <button v-if="search || filterCategory" type="button" class="empty-reset" @click="clearFilters">
        Clear filters
      </button>
    </div>

    <!-- DELETE CONFIRMATION MODAL -->
    <transition name="modal-fade">
      <div
        v-if="showDelete"
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-desc"
        @click.self="closeDeleteModal"
        @keydown="onModalKeydown"
      >
        <div class="delete-modal" ref="deleteModalRef">
          <h2 id="delete-modal-title" class="delete-title">Delete product?</h2>
          <p id="delete-modal-desc" class="delete-text">
            <strong v-if="deleteProductName">{{ deleteProductName }}</strong>
            <template v-if="deleteProductName"><br /></template>
            This cannot be undone. The product will be permanently removed from your store.
          </p>
          <div class="delete-actions">
            <button type="button" class="cancel-btn" @click="closeDeleteModal">
              Cancel
            </button>
            <button
              type="button"
              class="danger-btn"
              :disabled="deleteInProgress"
              @click="deleteProductConfirmed"
            >
              <span v-if="deleteInProgress" class="btn-spinner"></span>
              <span v-else>Delete permanently</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import { useAdminAuthStore } from "@/authen/adminAuth";
import globalToastMixin from "@/mixins/globalToastMixin";
import { dispatchAppToast } from "@/utils/toast";
import { buildApiUrl } from "@/config/api";

export default {
  name: "ProDucts",
  mixins: [globalToastMixin],

  data() {
    return {
      loading: false,
      products: [],
      search: "",
      sortBy: "name",
      filterCategory: "",

      page: 1,
      perPage: 8,

      /* DELETE MODAL */
      showDelete: false,
      deleteId: null,
      deleteInProgress: false,

      placeholderImage: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23f0f0f0' width='200' height='200'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='14' font-family='sans-serif'%3ENo image%3C/text%3E%3C/svg%3E",

      categoryMap: {
        1000: "Football",
        2000: "Gym & Fitness",
        3000: "Athletics & Running",
        4000: "Basketball",
        5000: "Volleyball",
        6000: "Rugby",
        7000: "Racket Sports",
        8000: "Cricket",
        9000: "Golf",
        10000: "Swimming",
        11000: "Cycling",
        13000: "Archery",
        14000: "Hockey",
        15000: "Combat Sports",
        16000: "Equestrian",
        17000: "Adventure & Outdoor",
        19000: "Niche & Regional Games",
      },
    };
  },

  computed: {
    filteredProducts() {
      let list = [...this.products];

      if (this.search.trim()) {
        const s = this.search.toLowerCase();
        list = list.filter((p) => p.name.toLowerCase().includes(s));
      }

      if (this.filterCategory !== "") {
        list = list.filter((p) => String(p.category_id) === String(this.filterCategory));
      }

      switch (this.sortBy) {
        case "price_low": list.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0)); break;
        case "price_high": list.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0)); break;
        case "brand": list.sort((a, b) => (a.brand || "").localeCompare(b.brand || "")); break;
        case "date_new": list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
        case "date_old": list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); break;
        default: list.sort((a, b) => a.name.localeCompare(b.name));
      }

      return list;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredProducts.length / this.perPage));
    },

    paginatedProducts() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredProducts.slice(start, start + this.perPage);
    },

    resultsSummary() {
      const total = this.filteredProducts.length;
      if (total === 0) return "No products";
      const from = (this.page - 1) * this.perPage + 1;
      const to = Math.min(this.page * this.perPage, total);
      return total <= this.perPage
        ? `${total} product${total === 1 ? "" : "s"}`
        : `Showing ${from}–${to} of ${total} products`;
    },

    deleteProductName() {
      if (!this.deleteId) return "";
      const p = this.products.find((x) => x.item_id === this.deleteId);
      return p ? p.name : "";
    },
  },

  watch: {
    filterCategory() {
      this.page = 1;
    },
    sortBy() {
      this.page = 1;
    },
    search() {
      this.page = 1;
    },
    perPage() {
      this.page = 1;
    },
  },

  methods: {
    showToast(message, type = "success") {
      dispatchAppToast(message, "", type);
    },

    onImageError(e) {
      if (e.target) e.target.src = this.placeholderImage;
    },

    formatPrice(product) {
      const price = product.price != null ? Number(product.price) : 0;
      const currency = product.currency || "KES";
      const discount = product.discount_price != null ? Number(product.discount_price) : null;
      if (discount != null && discount < price) {
        return `${currency} ${discount.toLocaleString()}`;
      }
      return `${currency} ${price.toLocaleString()}`;
    },

    priceWas(product) {
      const price = product.price != null ? Number(product.price) : 0;
      const discount = product.discount_price != null ? Number(product.discount_price) : null;
      const currency = product.currency || "KES";
      if (discount != null && discount < price && price > 0) {
        return `${currency} ${price.toLocaleString()}`;
      }
      return null;
    },

    isLowStock(product) {
      const qty = product.stock_quantity != null ? Number(product.stock_quantity) : 0;
      const threshold = product.low_stock_threshold != null ? Number(product.low_stock_threshold) : 10;
      return qty <= threshold && qty > 0;
    },

    categoryLabel(product) {
      const id = product.category_id != null ? String(product.category_id) : "";
      return this.categoryMap[id] || "";
    },

    clearFilters() {
      this.search = "";
      this.filterCategory = "";
      this.page = 1;
    },

    onModalKeydown(e) {
      if (e.key === "Escape") this.closeDeleteModal();
    },

    getAdminToken() {
      const adminStore = useAdminAuthStore();
      const raw = adminStore?.accessToken || localStorage.getItem("adminAccessToken");
      return typeof raw === "string" ? raw.trim() : "";
    },

    async fetchProducts() {
      try {
        this.loading = true;
        const token = this.getAdminToken();
        if (!token) {
          this.showToast("Please sign in as admin to view products.", "error");
          return;
        }

        const res = await fetch(buildApiUrl("/post/get_all"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          this.showToast("Session expired. Please sign in again.", "error");
          this.$router.push({ name: "AdminLoginPanel" });
          return;
        }
        if (res.status === 403) {
          this.showToast("Only super admin or support can view all products.", "error");
          return;
        }
        if (!res.ok) throw new Error("Fetch failed");

        const data = await res.json();
        this.products = data.products || [];
      } catch (err) {
        console.error(err);
        this.showToast("Could not load products.", "error");
      } finally {
        this.loading = false;
      }
    },

    /* EDIT MODAL */
    openEdit(product) {
      this.$router.push({ name: "EditProduct", params: { id: product.item_id } });
    },


    async saveEdit() {
      try {
        const token = this.getAdminToken();
        const res = await fetch(
          buildApiUrl(`/post/product/${this.editingId}/update`),
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(this.editForm),
          }
        );

        if (!res.ok) throw new Error("Update failed");

        const i = this.products.findIndex((p) => p.item_id === this.editingId);
        if (i !== -1) this.products[i] = { ...this.editForm };

        this.closeEdit();
        this.showToast("Product updated.", "success");
      } catch (err) {
        console.error(err);
        this.showToast("Failed to update product.", "error");
      }
    },

    /* DELETE MODAL */
    openDeleteModal(product) {
      this.deleteId = product.item_id;
      this.showDelete = true;
    },

    closeDeleteModal() {
      this.showDelete = false;
      this.deleteId = null;
    },

    async deleteProductConfirmed() {
      try {
        this.deleteInProgress = true;
        const token = this.getAdminToken();
        const res = await fetch(
          buildApiUrl(`/post/product/${this.deleteId}/delete`),
          {
            method: "DELETE",
            ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          }
        );

        if (!res.ok) throw new Error("Delete failed");

        this.products = this.products.filter((p) => p.item_id !== this.deleteId);
        this.closeDeleteModal();
        this.showToast("Product deleted.", "success");
      } catch (err) {
        console.error(err);
        this.showToast("Failed to delete product.", "error");
      } finally {
        this.deleteInProgress = false;
      }
    },
  },

  mounted() {
    this.fetchProducts();
  },
};
</script>

<style scoped>
/* ---- General ---- */
.products-wrapper {
  width: 100%;
  padding: 2rem;
  min-height: 100%;
  background: var(--background-color2, #f8fafc);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  font-family: "Poppins", sans-serif;
  animation: fadeIn 0.4s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ---- Header ---- */
.header {
  margin-bottom: 2rem;
}

.header-top {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color1, #0f172a);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.results-summary {
  font-size: 0.95rem;
  color: var(--text-color2, #64748b);
  margin: 0;
  opacity: 0.9;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 320px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2, #94a3b8);
  font-size: 0.95rem;
  pointer-events: none;
}

.search-box {
  width: 100%;
  padding: 12px 40px 12px 42px;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover, #e2e8f0);
  background: var(--background-color1, #ffffff);
  color: var(--text-color1, #334155);
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-box::placeholder {
  color: var(--text-color2, #94a3b8);
}

.search-box:focus {
  outline: none;
  background: var(--background-color1, #ffffff);
  border-color: var(--border-color-hover, #6366f1);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.1);
}

.clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  background: var(--background-color3, #e2e8f0);
  color: var(--text-color2, #64748b);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clear-search:hover {
  background: var(--border-color-hover, #cbd5e1);
  color: var(--text-color1, #334155);
}

.sort-select {
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover, #e2e8f0);
  background: var(--background-color1, #ffffff);
  color: var(--text-color1, #334155);
  font-size: 0.95rem;
  min-width: 140px;
  transition: all 0.3s ease;
}

.sort-select:focus {
  outline: none;
  background: var(--background-color1, #ffffff);
  border-color: var(--border-color-hover, #6366f1);
  box-shadow: 0 0 0 3px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.1);
}

.per-page-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.per-page-label {
  font-size: 0.9rem;
  color: var(--text-color2, #64748b);
  white-space: nowrap;
  font-weight: 500;
}

.per-page-select {
  min-width: 70px;
}

/* ---- Skeleton ---- */
.skeleton-grid {
  pointer-events: none;
}

.skeleton-card {
  cursor: default;
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 1 / 1.15;
  background: linear-gradient(90deg, var(--background-color2, #f1f5f9) 25%, var(--background-color3, #e2e8f0) 50%, var(--background-color2, #f1f5f9) 75%);
  background-size: 200% 100%;
  animation: skeleton 1.2s ease-in-out infinite;
}

.skeleton-details {
  padding: 0.9rem 1rem;
  background: var(--background-color1, #ffffff);
}

.skeleton-line {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--background-color2, #f1f5f9) 25%, var(--background-color3, #e2e8f0) 50%, var(--background-color2, #f1f5f9) 75%);
  background-size: 200% 100%;
  animation: skeleton 1.2s ease-in-out infinite;
}

.skeleton-title {
  width: 85%;
  margin-bottom: 8px;
}

.skeleton-price {
  width: 45%;
  height: 12px;
}

@keyframes skeleton {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* ---- Product grid ---- */
.products-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.25s ease;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

/* ---- Product card ---- */
.product-card {
  background: var(--background-color1, #ffffff);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  border: 1px solid var(--border-color-hover, #e2e8f0);
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 22px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.12);
  border-color: var(--border-color-hover, #6366f1);
}

.image-container {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1.15;
  overflow: hidden;
  background: var(--background-color2, #f8fafc);
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.35s ease;
}

.product-card:hover .product-img {
  transform: scale(1.05);
}

.badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  z-index: 1;
}

.badge-inactive {
  background: #64748b;
  color: #fff;
}

.badge-low-stock {
  background: #f59e0b;
  color: #fff;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.25s ease;
}

.image-container:hover .overlay {
  opacity: 1;
}

.overlay-icons {
  display: flex;
  gap: 1rem;
}

.overlay-btn {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  color: var(--text-color1, #475569);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.overlay-btn:hover {
  transform: scale(1.08) translateY(-2px);
  background: var(--background-color1, #ffffff);
  color: var(--border-color-hover, #6366f1);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.2);
}

.overlay-btn-delete:hover {
  background: rgba(255, 71, 87, 0.15);
  color: #ff4757;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.2);
}

.overlay-btn i {
  font-size: 1.1rem;
}

.product-details {
  padding: 0.9rem 1rem;
}

.product-brand {
  font-size: 0.75rem;
  color: var(--text-color2, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 4px 0;
  font-weight: 500;
}

.product-name {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 2px 0;
  color: var(--text-color1, #0f172a);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-category {
  font-size: 0.8rem;
  color: var(--text-color2, #94a3b8);
  margin: 0 0 6px 0;
}

.product-price {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--border-color-hover, #6366f1);
  margin: 0;
}

.price-was {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-color2, #94a3b8);
  text-decoration: line-through;
  margin-left: 6px;
}

/* ---- Pagination ---- */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 12px 20px;
  background: var(--background-color1, #ffffff);
  border: 1px solid var(--border-color-hover, #e2e8f0);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color1, #475569);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--border-color-hover, #6366f1), var(--border-color-hover1, #818cf8));
  color: #ffffff;
  border-color: var(--border-color-hover, #6366f1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.3);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.95rem;
  color: var(--text-color2, #64748b);
  font-weight: 500;
}

.pagination-info strong {
  color: var(--text-color1, #334155);
}

/* ---- Empty state ---- */
.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  margin-top: 2rem;
  background: var(--background-color1, #ffffff);
  border-radius: 16px;
  border: 1px dashed var(--border-color-hover, #e2e8f0);
  box-shadow: 0 4px 16px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.05);
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-color2, #cbd5e1);
  margin-bottom: 1rem;
}

.empty-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--text-color1, #475569);
  margin: 0 0 0.5rem 0;
}

.empty-text {
  font-size: 0.95rem;
  color: var(--text-color2, #64748b);
  margin: 0 0 1.5rem 0;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

.empty-reset {
  padding: 12px 24px;
  background: var(--background-color1, #ffffff);
  border: 1px solid var(--border-color-hover, #e2e8f0);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--border-color-hover, #6366f1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.empty-reset:hover {
  background: linear-gradient(135deg, var(--border-color-hover, #6366f1), var(--border-color-hover1, #818cf8));
  color: #ffffff;
  border-color: var(--border-color-hover, #6366f1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--border-color-hover-rgb, 99, 102, 241), 0.2);
}

/* ---- Delete modal ---- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-overlay:focus {
  outline: none;
}

.delete-modal {
  width: 100%;
  max-width: 450px;
  background: var(--background-color1, #ffffff);
  border-radius: 16px;
  padding: 2rem 1.75rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalIn 0.3s ease;
  border: 1px solid var(--border-color-hover, #e2e8f0);
}

@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .delete-modal,
.modal-fade-leave-active .delete-modal {
  transition: transform 0.2s ease;
}

.modal-fade-enter-from .delete-modal,
.modal-fade-leave-to .delete-modal {
  transform: scale(0.96) translateY(-8px);
}

.delete-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color1, #0f172a);
  margin: 0 0 1rem 0;
}

.delete-text {
  font-size: 1rem;
  color: var(--text-color2, #475569);
  line-height: 1.6;
  margin: 0 0 1.75rem 0;
}

.delete-text strong {
  color: var(--text-color1, #0f172a);
  font-weight: 600;
}

.delete-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.cancel-btn {
  padding: 12px 20px;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover, #e2e8f0);
  background: var(--background-color1, #ffffff);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-color1, #475569);
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: var(--background-color2, #f8fafc);
  border-color: var(--border-color-hover, #cbd5e1);
  transform: translateY(-1px);
}

.danger-btn {
  padding: 12px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #ff4757, #ff6b81);
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 160px;
}

.danger-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff6b81, #ff4757);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 71, 87, 0.4);
}

.danger-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ---- Responsive ---- */
@media (max-width: 768px) {
  .products-wrapper {
    padding: 1rem;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .search-wrap {
    max-width: none;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 1rem;
  }
}
</style>
